import {
  type EncryptedWrappingChallenge,
  type EnrollmentTranscript,
  answerWrappingKeyChallenge,
  generateDeviceKeyPairs,
  signEnrollmentTranscript,
} from "../../src/crypto/index.js";
import {
  type DeviceKeyStore,
  IndexedDbDeviceStore,
} from "./device-store.js";
import "./styles.css";

const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43,512}$/u;

export interface InvitationParticipant {
  readonly partyRef: string;
  readonly status: "pending" | "confirmed";
}

export interface InvitationOperator {
  readonly operatorId: string;
  readonly organizationId: string;
  readonly displayName: string;
  readonly origin: string;
}

export interface EnrollmentContext {
  readonly version: 1;
  readonly invitationId: string;
  readonly relationshipId: string;
  readonly audience: string;
  readonly relyingOrganizationId: string;
  readonly partyId: string;
  readonly partyRole: "owner" | "participant";
  readonly purpose: string;
  readonly scope: string;
  readonly permissions: readonly ("open" | "lock")[];
  readonly challengeId: string;
  readonly challenge: string;
  readonly issuedAtMs: number;
  readonly expiresAtMs: number;
}

export interface InvitationSession {
  readonly relationshipId: string;
  readonly partyId: string;
  readonly partyRef: string;
  readonly relyingSystemName: string;
  readonly purpose: string;
  readonly participants: readonly InvitationParticipant[];
  readonly operator: InvitationOperator;
  readonly enrollmentContext: EnrollmentContext;
}

export interface ConfirmFlowDependencies {
  readonly fetch: typeof fetch;
  readonly deviceStore: DeviceKeyStore;
  readonly nowMs?: () => number;
}

export interface FragmentLocation {
  readonly hash: string;
  readonly pathname: string;
  readonly search: string;
}

export interface FragmentHistory {
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
}

interface EnrollmentResult {
  readonly enrollmentId: string;
  readonly wrappingChallenge: EncryptedWrappingChallenge;
  readonly expiresAtMs: number;
}

export class ConfirmPageError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "ConfirmPageError";
  }
}

export function consumeInvitationToken(
  location: FragmentLocation,
  history: FragmentHistory,
): string {
  const fragment = location.hash.startsWith("#")
    ? location.hash.slice(1)
    : location.hash;
  history.replaceState(null, "", `${location.pathname}${location.search}`);

  const parameters = new URLSearchParams(fragment);
  if (
    [...parameters].length !== 1 ||
    [...parameters.keys()].some((key) => key !== "invite")
  ) {
    throw new ConfirmPageError("The invitation link is invalid");
  }
  const token = parameters.get("invite");
  if (token === null || !TOKEN_PATTERN.test(token)) {
    throw new ConfirmPageError("The invitation link is invalid");
  }
  return token;
}

export async function loadInvitationSession(
  invitationToken: string,
  dependencies: ConfirmFlowDependencies,
): Promise<InvitationSession> {
  assertToken(invitationToken);
  return normalizeInvitationSession(
    await requestJson(
      dependencies.fetch,
      "/v1/invitations/session",
      "POST",
      invitationToken,
    ),
  );
}

export async function completeInvitationConfirmation(
  invitationToken: string,
  session: InvitationSession,
  dependencies: ConfirmFlowDependencies,
): Promise<void> {
  assertToken(invitationToken);
  const nowMs = dependencies.nowMs?.() ?? Date.now();
  if (
    nowMs < session.enrollmentContext.issuedAtMs ||
    nowMs >= session.enrollmentContext.expiresAtMs
  ) {
    throw new ConfirmPageError("This confirmation session has expired");
  }

  const stored = await dependencies.deviceStore.load(
    session.relationshipId,
    session.partyId,
  );
  const keys = stored?.keys ?? (await generateDeviceKeyPairs());
  if (stored === undefined) {
    await dependencies.deviceStore.save({
      relationshipId: session.relationshipId,
      partyId: session.partyId,
      createdAtMs: nowMs,
      keys,
    });
  }

  const transcript: EnrollmentTranscript = {
    ...session.enrollmentContext,
    device: keys.publicKeys,
  };
  const proof = await signEnrollmentTranscript(
    transcript,
    keys.signingKeyPair.privateKey,
  );
  const enrollment = normalizeEnrollmentResult(
    await requestJson(
      dependencies.fetch,
      "/v1/invitations/enroll",
      "POST",
      invitationToken,
      { proof },
    ),
  );
  if (nowMs >= enrollment.expiresAtMs) {
    throw new ConfirmPageError("The wrapping-key challenge has expired");
  }
  const wrappingProof = await answerWrappingKeyChallenge(
    keys.wrappingKeyPair.privateKey,
    enrollment.wrappingChallenge,
  );
  await requestJson(
    dependencies.fetch,
    "/v1/invitations/confirm",
    "POST",
    invitationToken,
    { enrollmentId: enrollment.enrollmentId, wrappingProof },
  );
}

export async function bootConfirmPage(
  documentValue: Document = document,
  windowValue: Window = window,
  dependencies: ConfirmFlowDependencies = {
    fetch: windowValue.fetch.bind(windowValue),
    deviceStore: new IndexedDbDeviceStore(windowValue.indexedDB),
  },
): Promise<void> {
  const view = createConfirmView(documentValue);
  let invitationToken: string;
  try {
    invitationToken = consumeInvitationToken(windowValue.location, windowValue.history);
  } catch (error) {
    showError(view, userMessage(error));
    return;
  }

  showStatus(view, "Checking this invitation…");
  let session: InvitationSession;
  try {
    session = await loadInvitationSession(invitationToken, dependencies);
  } catch (error) {
    showError(view, userMessage(error));
    return;
  }
  view.relyingSystem.textContent =
    `${session.relyingSystemName} (${session.enrollmentContext.relyingOrganizationId})`;
  view.purpose.textContent = session.purpose;
  view.scope.textContent = session.enrollmentContext.scope;
  view.permissions.textContent = describePermissions(session.enrollmentContext.permissions);
  view.role.textContent = session.enrollmentContext.partyRole === "owner" ? "Owner" : "Participant";
  view.operator.textContent = session.operator.displayName;
  const confirmed = session.participants.filter(
    (participant) => participant.status === "confirmed",
  ).length;
  view.participants.textContent = `${confirmed} of ${session.participants.length} people confirmed`;
  view.button.disabled = false;
  showStatus(view, "Review the details, then confirm this device.");

  view.button.addEventListener(
    "click",
    () => {
      view.button.disabled = true;
      showStatus(view, "Creating a secure device key…");
      void completeInvitationConfirmation(invitationToken, session, dependencies)
        .then(() => {
          view.button.hidden = true;
          showStatus(view, "This device is confirmed. You may close this window.");
        })
        .catch((error: unknown) => {
          showError(view, userMessage(error));
        });
    },
    { once: true },
  );
}

interface ConfirmView {
  readonly relyingSystem: HTMLElement;
  readonly purpose: HTMLElement;
  readonly scope: HTMLElement;
  readonly permissions: HTMLElement;
  readonly role: HTMLElement;
  readonly operator: HTMLElement;
  readonly participants: HTMLElement;
  readonly button: HTMLButtonElement;
  readonly status: HTMLElement;
}

function createConfirmView(documentValue: Document): ConfirmView {
  const main = documentValue.createElement("main");
  main.className = "page-shell";
  const card = documentValue.createElement("section");
  card.className = "card";
  card.setAttribute("aria-labelledby", "confirm-heading");
  const eyebrow = documentValue.createElement("p");
  eyebrow.className = "eyebrow";
  eyebrow.textContent = "Independent confirmation";
  const heading = documentValue.createElement("h1");
  heading.id = "confirm-heading";
  heading.textContent = "Confirm this device";
  const lead = documentValue.createElement("p");
  lead.className = "lead";
  lead.textContent =
    "This creates a private key in this browser. The key never goes to the storage company or Witness.";
  const details = documentValue.createElement("dl");
  details.className = "details";
  const relyingSystem = addDetail(documentValue, details, "Service");
  const purpose = addDetail(documentValue, details, "Purpose");
  const scope = addDetail(documentValue, details, "Access covered");
  const permissions = addDetail(documentValue, details, "Allowed actions");
  const role = addDetail(documentValue, details, "Your role");
  const operator = addDetail(documentValue, details, "Independent operator");
  const participants = addDetail(documentValue, details, "Progress");
  const button = documentValue.createElement("button");
  button.className = "primary";
  button.type = "button";
  button.disabled = true;
  button.textContent = "Confirm this device";
  const status = documentValue.createElement("p");
  status.className = "status";
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  card.append(eyebrow, heading, lead, details, button, status);
  main.append(card);
  documentValue.body.replaceChildren(main);
  return {
    relyingSystem,
    purpose,
    scope,
    permissions,
    role,
    operator,
    participants,
    button,
    status,
  };
}

function addDetail(documentValue: Document, list: HTMLDListElement, label: string): HTMLElement {
  const wrapper = documentValue.createElement("div");
  const term = documentValue.createElement("dt");
  term.textContent = label;
  const description = documentValue.createElement("dd");
  description.textContent = "—";
  wrapper.append(term, description);
  list.append(wrapper);
  return description;
}

function showStatus(view: ConfirmView, message: string): void {
  view.status.classList.remove("error");
  view.status.setAttribute("role", "status");
  view.status.textContent = message;
}

function showError(view: ConfirmView, message: string): void {
  view.status.classList.add("error");
  view.status.setAttribute("role", "alert");
  view.status.textContent = message;
}

function describePermissions(permissions: readonly ("open" | "lock")[]): string {
  const labels = permissions.map((permission) =>
    permission === "open" ? "Open protected documents" : "Protect documents (owner only)",
  );
  return labels.join("; ");
}

async function requestJson(
  fetchValue: typeof fetch,
  url: string,
  method: "GET" | "POST",
  bearerToken: string,
  body?: object,
): Promise<unknown> {
  const response = await fetchValue(url, {
    method,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${bearerToken}`,
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    cache: "no-store",
    credentials: "omit",
    redirect: "error",
    referrerPolicy: "no-referrer",
  });
  if (!response.ok) {
    throw new ConfirmPageError("Witness could not complete this request");
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

function normalizeInvitationSession(value: unknown): InvitationSession {
  const record = requireRecord(value, "invitation session");
  const participantsValue = record.participants;
  if (!Array.isArray(participantsValue) || participantsValue.length < 2 || participantsValue.length > 100) {
    throw new ConfirmPageError("The invitation session is malformed");
  }
  const participants = participantsValue.map((participantValue) => {
    const participant = requireRecord(participantValue, "participant");
    const status = requireString(participant.status, "participant status", 16);
    if (status !== "pending" && status !== "confirmed") {
      throw new ConfirmPageError("The invitation participant status is invalid");
    }
    const normalizedStatus: InvitationParticipant["status"] = status;
    return {
      partyRef: requireString(participant.partyRef, "participant reference", 256),
      status: normalizedStatus,
    };
  });
  const operatorValue = requireRecord(record.operator, "operator");
  const origin = requireHttpsOrigin(operatorValue.origin, "operator origin");
  const contextValue = requireRecord(record.enrollmentContext, "enrollment context");
  const issuedAtMs = requireTimestamp(contextValue.issuedAtMs, "enrollment issued time");
  const expiresAtMs = requireTimestamp(contextValue.expiresAtMs, "enrollment expiry");
  if (issuedAtMs >= expiresAtMs) {
    throw new ConfirmPageError("The enrollment validity window is invalid");
  }
  if (contextValue.version !== 1) {
    throw new ConfirmPageError("The enrollment context version is unsupported");
  }
  const relationshipId = requireIdentifier(record.relationshipId, "relationship id");
  const partyId = requireIdentifier(record.partyId, "party id");
  const purpose = requireString(record.purpose, "purpose", 1024);
  const contextRelationshipId = requireIdentifier(
    contextValue.relationshipId,
    "enrollment relationship id",
  );
  const contextPartyId = requireIdentifier(
    contextValue.partyId,
    "enrollment party id",
  );
  const contextPurpose = requireString(
    contextValue.purpose,
    "enrollment purpose",
    1024,
  );
  const partyRole = requireString(contextValue.partyRole, "enrollment party role", 32);
  if (partyRole !== "owner" && partyRole !== "participant") {
    throw new ConfirmPageError("The enrollment party role is invalid");
  }
  if (
    contextRelationshipId !== relationshipId ||
    contextPartyId !== partyId ||
    contextPurpose !== purpose
  ) {
    throw new ConfirmPageError("The invitation details do not match the signed enrollment context");
  }
  const permissions = normalizePermissions(contextValue.permissions);
  return {
    relationshipId,
    partyId,
    partyRef: requireString(record.partyRef, "party reference", 256),
    relyingSystemName: requireString(record.relyingSystemName, "relying system name", 256),
    purpose,
    participants,
    operator: {
      operatorId: requireIdentifier(operatorValue.operatorId, "operator id"),
      organizationId: requireIdentifier(operatorValue.organizationId, "operator organization id"),
      displayName: requireString(operatorValue.displayName, "operator display name", 256),
      origin,
    },
    enrollmentContext: {
      version: 1,
      invitationId: requireIdentifier(contextValue.invitationId, "invitation id"),
      relationshipId: contextRelationshipId,
      audience: requireHttpsOrigin(contextValue.audience, "enrollment audience"),
      relyingOrganizationId: requireIdentifier(
        contextValue.relyingOrganizationId,
        "relying organization id",
      ),
      partyId: contextPartyId,
      partyRole,
      purpose: contextPurpose,
      scope: requireString(contextValue.scope, "enrollment scope", 256),
      permissions,
      challengeId: requireIdentifier(contextValue.challengeId, "challenge id"),
      challenge: requireString(contextValue.challenge, "challenge", 256),
      issuedAtMs,
      expiresAtMs,
    },
  };
}

function normalizePermissions(value: unknown): readonly ("open" | "lock")[] {
  if (!Array.isArray(value) || value.length < 1 || value.length > 2) {
    throw new ConfirmPageError("The enrollment permissions are invalid");
  }
  const permissions = new Set<"open" | "lock">();
  for (const candidate of value) {
    if (candidate !== "open" && candidate !== "lock") {
      throw new ConfirmPageError("The enrollment permissions are invalid");
    }
    if (permissions.has(candidate)) {
      throw new ConfirmPageError("The enrollment permissions are duplicated");
    }
    permissions.add(candidate);
  }
  return (["open", "lock"] as const).filter((permission) => permissions.has(permission));
}

function normalizeEnrollmentResult(value: unknown): EnrollmentResult {
  const record = requireRecord(value, "enrollment result");
  return {
    enrollmentId: requireIdentifier(record.enrollmentId, "enrollment id"),
    wrappingChallenge: record.wrappingChallenge as EncryptedWrappingChallenge,
    expiresAtMs: requireTimestamp(record.expiresAt, "wrapping challenge expiry"),
  };
}

function requireRecord(value: unknown, label: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new ConfirmPageError(`${label} is malformed`);
  }
  return value as Record<string, unknown>;
}

function requireString(value: unknown, label: string, maximum: number): string {
  if (typeof value !== "string" || value.length === 0 || value.length > maximum) {
    throw new ConfirmPageError(`${label} is invalid`);
  }
  return value;
}

function requireIdentifier(value: unknown, label: string): string {
  const result = requireString(value, label, 256);
  if (!/^[A-Za-z0-9._~:-]+$/u.test(result)) {
    throw new ConfirmPageError(`${label} is invalid`);
  }
  return result;
}

function requireTimestamp(value: unknown, label: string): number {
  const result =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Date.parse(value)
        : Number.NaN;
  if (!Number.isSafeInteger(result) || result < 0) {
    throw new ConfirmPageError(`${label} is invalid`);
  }
  return result;
}

function requireHttpsOrigin(value: unknown, label: string): string {
  const result = requireString(value, label, 512);
  let url: URL;
  try {
    url = new URL(result);
  } catch {
    throw new ConfirmPageError(`${label} is invalid`);
  }
  if (!isSecureOrLocalOrigin(url) || url.origin !== result) {
    throw new ConfirmPageError(`${label} is invalid`);
  }
  return result;
}

function isSecureOrLocalOrigin(url: URL): boolean {
  return (
    url.protocol === "https:" ||
    (url.protocol === "http:" &&
      (url.hostname === "127.0.0.1" || url.hostname === "localhost"))
  );
}

function assertToken(token: string): void {
  if (!TOKEN_PATTERN.test(token)) {
    throw new ConfirmPageError("The invitation token is invalid");
  }
}

function userMessage(error: unknown): string {
  if (error instanceof ConfirmPageError) {
    return error.message;
  }
  return "Witness could not confirm this device. Please try the invitation again.";
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  void bootConfirmPage();
}
